import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { Settings as SettingsIcon, Bell, Mail, MessageSquare, Phone } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import { billReminderSettingsAPI } from '../services/api';

const TABS = [
  { id: 'reminders', label: 'Automated Bill Reminders', icon: Bell },
];

export default function Settings() {
  const [activeTab, setActiveTab] = useState('reminders');
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    billReminderSettingsAPI.get()
      .then(({ data }) => setSettings(data))
      .catch(() => toast.error('Could not load settings'))
      .finally(() => setLoading(false));
  }, []);

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await billReminderSettingsAPI.update(settings);
      setSettings(data);
      toast.success('Settings saved');
    } catch {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6">
      <PageHeader
        title="Settings"
        subtitle="Configure firm-wide billing and reminder preferences"
        icon={<SettingsIcon size={20} />}
      />

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200 mb-6">
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.id
                ? 'border-blue-600 text-blue-700'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <tab.icon size={15} />
            {tab.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40">
          <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full" />
        </div>
      ) : (
        <>
          {activeTab === 'reminders' && settings && (
            <div className="max-w-2xl space-y-6">
              {/* Master toggle */}
              <div className="card p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-gray-800">Enable Automated Bill Reminders</p>
                    <p className="text-sm text-gray-500 mt-0.5">
                      Automatically notify clients with unpaid invoices. Per-client toggle available on the client profile.
                    </p>
                  </div>
                  <button
                    onClick={() => handleChange('enabled', !settings.enabled)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      settings.enabled ? 'bg-blue-600' : 'bg-gray-300'
                    }`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      settings.enabled ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>
              </div>

              {/* Timing */}
              <div className="card p-5 space-y-4">
                <h3 className="font-semibold text-gray-800">Reminder Timing</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="label">First reminder (days after bill issued)</label>
                    <input
                      type="number"
                      min="1"
                      className="input"
                      value={settings.first_reminder_days}
                      onChange={e => handleChange('first_reminder_days', parseInt(e.target.value) || 1)}
                    />
                  </div>
                  <div>
                    <label className="label">Repeat every (days)</label>
                    <input
                      type="number"
                      min="1"
                      className="input"
                      value={settings.repeat_every_days}
                      onChange={e => handleChange('repeat_every_days', parseInt(e.target.value) || 1)}
                    />
                  </div>
                </div>
                <p className="text-xs text-gray-500">Reminders stop automatically once an invoice is marked as paid.</p>
              </div>

              {/* Channels */}
              <div className="card p-5 space-y-3">
                <h3 className="font-semibold text-gray-800">Notification Channels</h3>
                <ChannelToggle
                  icon={Mail}
                  label="Email"
                  description="Send reminders via email"
                  enabled={settings.send_email}
                  onToggle={() => handleChange('send_email', !settings.send_email)}
                />
                <ChannelToggle
                  icon={MessageSquare}
                  label="WhatsApp"
                  description="Send via Twilio WhatsApp Business API"
                  enabled={settings.send_whatsapp}
                  onToggle={() => handleChange('send_whatsapp', !settings.send_whatsapp)}
                />
                <ChannelToggle
                  icon={Phone}
                  label="SMS"
                  description="Send via Twilio SMS"
                  enabled={settings.send_sms}
                  onToggle={() => handleChange('send_sms', !settings.send_sms)}
                />
              </div>

              {/* Reply-to email */}
              <div className="card p-5">
                <label className="label">Reply-to Email Address</label>
                <input
                  type="email"
                  className="input"
                  placeholder="billing@yourfirm.com"
                  value={settings.reply_to_email || ''}
                  onChange={e => handleChange('reply_to_email', e.target.value)}
                />
                <p className="text-xs text-gray-500 mt-1">Clients who reply to reminder emails will reach this address.</p>
              </div>

              <div className="flex justify-end">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="btn-primary px-6"
                >
                  {saving ? 'Saving…' : 'Save Settings'}
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function ChannelToggle({ icon: Icon, label, description, enabled, onToggle }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
      <div className="flex items-center gap-3">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${enabled ? 'bg-blue-100' : 'bg-gray-100'}`}>
          <Icon size={15} className={enabled ? 'text-blue-600' : 'text-gray-400'} />
        </div>
        <div>
          <p className="text-sm font-medium text-gray-800">{label}</p>
          <p className="text-xs text-gray-500">{description}</p>
        </div>
      </div>
      <button
        onClick={onToggle}
        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
          enabled ? 'bg-blue-600' : 'bg-gray-300'
        }`}
      >
        <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
          enabled ? 'translate-x-5' : 'translate-x-0.5'
        }`} />
      </button>
    </div>
  );
}
